import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/App';

const ANY = '__any';
const OUTCOMES = {
  valid: 'VALID', invalid_format: 'INVALID FORMAT', missing: 'INVALID FORMAT', expired: 'EXPIRED',
  future_transaction: 'FUTURE TRANSACTION', invalid_branch: 'INVALID BRANCH', already_used: 'ALREADY USED',
  invalid_transaction: 'INVALID TRANSACTION DATA', config_error: 'CONFIGURATION ERROR',
};
const METHOD_TEXT = { qr: 'QR', manual: 'Numeric' };

const toParams = (text) => {
  const t = text.trim();
  const query = t.includes('?') ? t.slice(t.indexOf('?') + 1) : t;
  return Object.fromEntries(new URLSearchParams(query));
};

const Detail = ({ label, value, testid }) => (
  <div className="flex gap-2">
    <dt className="w-40 shrink-0 text-[#64748B]">{label}</dt>
    <dd className="font-medium text-[#0F172A] break-all" data-testid={testid}>{value || '—'}</dd>
  </div>
);

const TestDetails = ({ method, d, branchName }) => (
  <dl className="mt-2 space-y-1" data-testid="access-test-context">
    {method === 'manual' && <Detail label="Normalized code" value={d.normalized_code} testid="access-test-normalized" />}
    <Detail label={method === 'manual' ? 'External / POS store code' : 'Incoming store code'} value={d.external_branch_id} testid="access-test-store" />
    <Detail label="Mapped existing branch"
      value={d.mapped_branch_id ? `${branchName(d.mapped_branch_id)}${d.branch_access_method ? ` (${METHOD_TEXT[d.branch_access_method]})` : ''}` : 'Not mapped'}
      testid="access-test-mapped-branch" />
    <Detail label="Transaction date / time" value={d.transaction_local && `${d.transaction_local.replace('T', ' ')} (${d.timezone})`} testid="access-test-datetime" />
    {method === 'manual' && <Detail label="Receipt / reference" value={d.reference_id} testid="access-test-reference" />}
  </dl>
);

// Dry-run the (unsaved) profile against a sample code or QR link. Never consumes, never logs.
export const AccessProfileTester = ({ buildPayload, branches }) => {
  const [method, setMethod] = useState('manual');
  const [sample, setSample] = useState('');
  const [branchId, setBranchId] = useState(ANY);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const branchName = (id) => (branches || []).find(b => b.branch_id === id)?.name || id;

  const run = async () => {
    setBusy(true);
    setRes(null);
    try {
      const body = { profile: buildPayload(), method, branch_id: branchId === ANY ? null : branchId,
        ...(method === 'manual' ? { access_code: sample } : { qr_params: toParams(sample) }) };
      const r = await fetch(`${API}/access-profiles/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      setRes(r.ok ? { ...data, method } : { valid: false, status: 'config_error', message: typeof data.detail === 'string' ? data.detail : 'Test failed' });
    } catch { setRes({ valid: false, status: 'config_error', message: 'Test failed' }); }
    setBusy(false);
  };

  return (
    <fieldset className="border border-dashed border-[#CBD5E1] rounded-lg p-3 space-y-2" data-testid="access-profile-tester">
      <legend className="text-xs font-bold text-[#0F172A] px-1">Test this profile</legend>
      <p className="text-[11px] text-[#64748B]">Testing never uses up a code.</p>
      <div className="flex flex-wrap gap-2">
        <div className="w-36">
          <Select value={method} onValueChange={(v) => { setMethod(v); setRes(null); }}>
            <SelectTrigger data-testid="access-test-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="manual">Manual code</SelectItem><SelectItem value="qr">QR link</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="access-test-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any branch</SelectItem>
              {(branches || []).map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input value={sample} onChange={(e) => setSample(e.target.value)} className="flex-1 min-w-[180px]"
          placeholder={method === 'manual' ? 'e.g. 360-190224-1311-11' : 'Full QR link or ?store_code=…&date=…'} data-testid="access-test-input" />
        <Button type="button" variant="outline" onClick={run} disabled={busy || !sample.trim()} data-testid="access-test-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
        </Button>
      </div>
      {res && (
        <div className={`text-xs rounded px-3 py-2 ${res.valid ? 'bg-[#ECFDF5] text-[#065F46]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`} data-testid="access-test-result">
          <p className="font-bold tracking-wide" data-testid="access-test-status">{OUTCOMES[res.status] || res.status}</p>
          <p>{res.message}</p>
          {res.details && <TestDetails method={res.method} d={res.details} branchName={branchName} />}
        </div>
      )}
    </fieldset>
  );
};
